// Kinio KMS 的電源（能源管理 KMS 面，plan SPEC #energy-ui-kms NRG-K1–K6、#energy-api；cloud/kmgr internal/api/power.go、internal/power/view.go）：
// UPS 一列、範圍摘要（Dashboard 電源列、VMS 用電頁並排區塊）、家戶的 UPS 與下游、〔登記下游〕、停電事故〔不用等這台〕、月報停電欄。
// 形狀照 kmgr 原樣（snake_case）；讀不到的欄位不帶（畫面寫「讀不到」，不寫 0）。
import { kmgrFetch } from './api';

export type UpsSource = 'normal' | 'battery' | 'bypass' | 'none' | 'other';

export interface UpsView {
  probe_id: string;
  home_id: string;
  org_id?: string;
  name?: string;
  /** 最近 5 分鐘有讀到；false = 讀不到（其餘欄位是最後一次讀到的） */
  readable: boolean;
  source: UpsSource;
  /** 自我測試中（轉電池不算停電） */
  self_test: boolean;
  on_battery_since?: string;
  battery_pct?: number;
  runtime_s?: number;
  load_pct?: number;
  output_w?: number;
  input_v?: number;
  battery_temp_c?: number;
  battery_status?: 'unknown' | 'normal' | 'low' | 'depleted';
  test_result?: 'pass' | 'warning' | 'error' | 'aborted' | 'in_progress' | 'none';
  alarms: string[];
  /** 登記的下游數（0 = 還沒登記：設定待辦） */
  downstream: number;
  /** 進行中的停電事故 */
  outage_incident_id?: string;
  at: string;
  /** 家戶 API 才有：登記的下游 */
  downstream_refs?: string[];
}

export interface PowerSummary {
  ups_total: number;
  ups_on_battery: number;
  ups_min_runtime_s?: number;
  ups_unreadable: number;
  ups_no_downstream: number;
  poe_over_warn: number;
  poe_denied: number;
}

export interface PowerNames {
  homes?: Record<string, string>;
  orgs?: Record<string, string>;
  users?: Record<string, string>;
  /** 設備 / 主機 / 探針的名稱（kmgr names.things / targets） */
  things?: Record<string, string>;
  targets?: Record<string, string>;
}

export interface OrgPower {
  org_id: string;
  checked_at: string;
  homes: number;
  summary: PowerSummary;
  ups: UpsView[];
  names?: PowerNames;
}

export interface PoeAlert {
  target_ref: string;
  kind: string;
  subject?: string;
  severity: string;
  incident_id?: string;
  opened_at: string;
}

export interface HomePower {
  home_id: string;
  checked_at: string;
  summary: PowerSummary;
  ups: (UpsView & { downstream_refs: string[] })[];
  poe_alerts: PoeAlert[];
  thresholds: { poe_warn_pct: number; poe_high_pct: number; source: 'default' | 'home'; managed_by_template?: string };
  can_configure: boolean;
  names?: PowerNames;
}

/** 停電事故的影響區（事故詳細的 power 區塊）。 */
export interface IncidentPower {
  phase: 'outage' | 'waiting' | 'restored';
  waiting: number;
  ups?: UpsView;
  /** 0 = 不知道哪些設備接在 UPS 後面 */
  downstream_total: number;
  offline: string[];
  downstream: string[];
  events: { started_at: string; ended_at?: string; escalated_at?: string }[];
  battery_history: { at: string; battery_pct?: number; runtime_s?: number }[];
}

export interface MonthlyOutages {
  has_ups: boolean;
  outages: number;
  longest_s: number;
  short_transfers: number;
  timeline: { probe_id: string; started_at: string; ended_at?: string; duration_s: number; all_recovered_at?: string; incident_id?: string; escalated_waiting?: boolean }[];
}

const enc = encodeURIComponent;

export const powerApi = {
  org: (api: string, orgId: string, scope?: string) => kmgrFetch<OrgPower>(api, `/v1/orgs/${enc(orgId)}/kmgr/power`, { query: { scope } }),
  home: (api: string, homeId: string) => kmgrFetch<HomePower>(api, `/v1/homes/${enc(homeId)}/kmgr/power`),
  putDownstream: (api: string, homeId: string, probeId: string, targets: string[]) =>
    kmgrFetch<{ home_id: string; probe_id: string; targets: string[]; changed: boolean; names?: PowerNames }>(api, `/v1/homes/${enc(homeId)}/kmgr/ups/${enc(probeId)}/downstream`, {
      method: 'PUT',
      body: { targets },
    }),
  skipWait: (api: string, homeId: string, incidentId: string, targetRef: string) =>
    kmgrFetch<unknown>(api, `/v1/homes/${enc(homeId)}/kmgr/incidents/${enc(incidentId)}/skip-wait`, { method: 'POST', body: { target_ref: targetRef } }),
  outages: (api: string, homeId: string, from: string, to: string) =>
    kmgrFetch<{ home_id: string; outages: MonthlyOutages }>(api, `/v1/homes/${enc(homeId)}/kmgr/power/outages`, { query: { from, to } }),
};

/** 摘要合併（多個組織）。 */
export function mergePowerSummary(list: PowerSummary[]): PowerSummary {
  const out: PowerSummary = { ups_total: 0, ups_on_battery: 0, ups_unreadable: 0, ups_no_downstream: 0, poe_over_warn: 0, poe_denied: 0 };
  for (const s of list) {
    out.ups_total += s.ups_total;
    out.ups_on_battery += s.ups_on_battery;
    out.ups_unreadable += s.ups_unreadable;
    out.ups_no_downstream += s.ups_no_downstream;
    out.poe_over_warn += s.poe_over_warn;
    out.poe_denied += s.poe_denied;
    if (s.ups_min_runtime_s !== undefined && (out.ups_min_runtime_s === undefined || s.ups_min_runtime_s < out.ups_min_runtime_s)) out.ups_min_runtime_s = s.ups_min_runtime_s;
  }
  return out;
}
